import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import creditCardType from 'credit-card-type';

import { useBookingSummary } from 'src/hooks/useBookingSummary';
import { useBookingSummaryKey } from 'src/hooks/useBookingSummary';
import { setCarouselPageIsCompletedAction } from 'src/components/shared/SciPage/SciPage.duck';
import { SciPagesEnum } from 'src/components/shared/SciPage/types';
import { useConfiguration } from 'src/hooks/useConfiguration';
import { minimumPaymentAmount } from 'src/constants';
import { useGetSections } from 'src/modules/documents/hooks/documents.hooks';

import { usePaymentSurcharge } from './usePaymentSurcharge';
import { usePaymentForm } from './usePaymentForm';
import { usePaymentIssuers } from './usePaymentIssuers';
import { useWindcave } from './useWindcave';
import { PaymentIssuerResponse, PaymentFormValues } from '../payment.type';
import { PaymentMethodEnum } from '../payment.enum';

export const usePayment = () => {
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const configuration = useConfiguration();
    const [paymentAmount, setPaymentAmount] = useState(0);
    const needToConsiderSurcharge =
        configuration.functionalities.payments.calculateSurcharge;

    const { data: documentSections, isLoading: documentSectionsIsLoading } =
        useGetSections();
    const anyDocuments = documentSections?.length > 0;

    const { data: bookingSummaryData, isLoading: isBookingSummaryLoading } =
        useBookingSummary();

    const originalAmount = bookingSummaryData?.bookingTotalPrice?.balance ?? 0;

    const { control, setValue, getValues, watch, handleSubmit, showTc } =
        usePaymentForm({ bookingSummaryData });

    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(
        getValues('paymentMethod'),
    );
    const [selectedPaymentIssuer, setSelectedPaymentIssuer] = useState(
        getValues('issuer'),
    );

    const onPaymentComplete = useCallback(
        (option?: { ignorePaymentMethod: boolean }) => {
            queryClient.invalidateQueries({ queryKey: [useBookingSummaryKey] });
            if (option?.ignorePaymentMethod) {
                dispatch(
                    setCarouselPageIsCompletedAction({
                        page: SciPagesEnum.Payment,
                    }),
                );
            } else {
                dispatch(
                    setCarouselPageIsCompletedAction({
                        page: SciPagesEnum.Payment,
                        urlParams: {
                            paymentMethod: selectedPaymentMethod,
                        },
                    }),
                );
            }
        },
        [dispatch, queryClient, selectedPaymentMethod],
    );

    const {
        mutate: windcaveMutate,
        isLoading: isPaymentInProgress,
        error: paymentError,
    } = useWindcave(onPaymentComplete);

    const onPaymentIssuersSuccess = (data: PaymentIssuerResponse[]) => {
        if (data?.length > 0) {
            setValue('issuer', '');
        }
    };

    const {
        isLoading: paymentIssuerLoading,
        data: paymentIssuers,
        error: paymentIssuerError,
    } = usePaymentIssuers(onPaymentIssuersSuccess);

    const {
        mutate: mutatePaymentSurcharge,
        data: paymentSurcharge,
        isLoading: paymentSurchargeIsLoading,
        error: paymentSurchargeError,
    } = usePaymentSurcharge();

    const watchPaymentDescription = useCallback(
        (data: any, info: any) => {
            if (needToConsiderSurcharge) {
                if (
                    info?.name === 'paymentMethod' &&
                    data?.paymentMethod === PaymentMethodEnum.Manual
                ) {
                    setPaymentAmount(
                        bookingSummaryData?.bookingTotalPrice?.balance ?? 0,
                    );
                } else if (
                    info?.name === 'issuer' ||
                    info?.name === 'paymentMethod'
                ) {
                    const issuerCode = paymentIssuers?.find(
                        (issuer) => issuer.name === data.issuer,
                    )?.code;
                    const paymentMethod = data?.paymentMethod;

                    if (issuerCode && paymentMethod) {
                        mutatePaymentSurcharge({
                            method: paymentMethod,
                            issuer: issuerCode,
                        });
                    }
                }
            }

            if (data.paymentMethod) {
                setSelectedPaymentMethod(data.paymentMethod);
            }
            if (data.issuer) {
                setSelectedPaymentIssuer(data.issuer);
            }
        },
        [
            bookingSummaryData?.bookingTotalPrice?.balance,
            paymentIssuers,
            mutatePaymentSurcharge,
            needToConsiderSurcharge,
        ],
    );

    useEffect(() => {
        const subscription = watch((data, info) =>
            watchPaymentDescription(data, info),
        );
        return () => subscription.unsubscribe();
    }, [watch, watchPaymentDescription]);

    const getIssuerByCardNumber = (
        cardNumber: any,
        paymentIssuers: PaymentIssuerResponse[] = [],
    ) => {
        const cardTypeObj = creditCardType(cardNumber);
        const cardType = cardTypeObj?.[0]?.niceType?.toLowerCase();

        return (
            paymentIssuers.find(
                (issuer) => cardType === issuer.code.toLowerCase(),
            )?.code ?? ''
        );
    };

    const onSubmit = async (data: PaymentFormValues) => {
        if (originalAmount < minimumPaymentAmount && !isBookingSummaryLoading) {
            // If nothing to pay redirect to confirmation
            onPaymentComplete({ ignorePaymentMethod: true });
            return;
        }

        const issuerCode =
            paymentIssuers?.find((issuer) => issuer.name === data.issuer)
                ?.code ??
            getIssuerByCardNumber(data.cardNumber, paymentIssuers);

        const paymentSurchargePayload = {
            method: data?.paymentMethod,
            issuer: issuerCode,
        };

        windcaveMutate({
            cardDetails: data,
            paymentSurchargePayload,
        });
    };

    useEffect(() => {
        // Initially set it to bookingSummaryAmount
        setPaymentAmount(originalAmount);

        if (
            originalAmount < minimumPaymentAmount &&
            anyDocuments &&
            !isBookingSummaryLoading &&
            !documentSectionsIsLoading
        ) {
            // If nothing to pay redirect to confirmation
            onPaymentComplete({ ignorePaymentMethod: true });
        }
    }, [
        originalAmount,
        isBookingSummaryLoading,
        documentSectionsIsLoading,
        anyDocuments,
        onPaymentComplete,
    ]);

    useEffect(() => {
        if (paymentSurcharge?.totalAmount) {
            setPaymentAmount(paymentSurcharge?.totalAmount);
        }
    }, [paymentSurcharge]);

    return {
        paymentAmount,
        paymentIssuers,
        paymentIssuerLoading,
        paymentSurcharge: paymentSurcharge?.amount,
        paymentSurchargeIsLoading,
        needToConsiderSurcharge,
        isPaymentInProgress,
        selectedPaymentMethod,
        selectedPaymentIssuer,
        currency: bookingSummaryData?.currency,
        paymentError,
        error: paymentIssuerError || paymentSurchargeError,
        control,
        showTc,
        onSubmit: handleSubmit(onSubmit),
        onSubmitManual: onPaymentComplete,
    };
};
